/* eslint-disable max-params */
/* eslint-disable complexity */
import configurations from '../utils/config/configurations'
import { initializeApp } from 'firebase/app'
import {
  Analytics,
  getAnalytics,
  logEvent,
  setCurrentScreen,
  setUserId,
  setUserProperties
} from 'firebase/analytics'
import moment, { Moment } from 'moment'
import { IBooking, IMileage } from '../stores/bookingTypes'
import { IUser } from '../stores/RegisterTypes'
import { postToApi } from './apiService'
import TagManager from 'react-gtm-module'
import ReactPixel from 'react-facebook-pixel'
import { IProduct } from '../stores/productStore'
import { ICity, ILocation } from '../stores/LocationStore'
import { FirebasePerformance, getPerformance } from 'firebase/performance'

let firebaseAnalytics: Analytics
let firebasePerformance: FirebasePerformance | null = null
let eventQueue:any[] = []
let gtmContainerLoaded = false

// Function to load GTM container dynamically
const loadGTMContainer = (consentAnalytics:string) => {
  if (!gtmContainerLoaded) {
    // Create a new script element for GTM
    const script = document.createElement('script')
    script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-KS9W7Z3' // Replace GTM-XXXXXX with your GTM container ID
    script.async = true
    // Append the script element to the document body
    document.body.appendChild(script)
    gtmContainerLoaded = true

    if (consentAnalytics === 'ACCEPT' && eventQueue.length > 0) {
      eventQueue.forEach((event) => {
        sendBookingAnalyticEvent(event.eventName, event.params)
      })
      // Clear the event queue after processing
      eventQueue = []
    }
  }
}

const getFirebaseAnalytics = (): Analytics => {
  if (configurations.FIREBASE_CONFIG) {
    const app = initializeApp(configurations.FIREBASE_CONFIG)

    if (
      process.env.REACT_APP_PLATFORM === 'uft' ||
      process.env.REACT_APP_PLATFORM === 'ufodrive'
    ) {
      const consent = (window as any)?.Osano.cm.getConsent().ANALYTICS

      if (consent === 'ACCEPT') {
        loadGTMContainer(consent)
        firebaseAnalytics = getAnalytics(app)
      }
    } else {
      firebaseAnalytics = getAnalytics(app)
    }
    firebasePerformance = getPerformance()
    firebasePerformance.instrumentationEnabled = true
    firebasePerformance.dataCollectionEnabled = true
  }

  return firebaseAnalytics
}

const sendEvent = async (
  eventName: 'click_info' | bookingEventType,
  params: any
) => {
  if (
    process.env.REACT_APP_PLATFORM === 'uft' ||
    process.env.REACT_APP_PLATFORM === 'ufodrive'
  ) {
    TagManager.dataLayer({
      dataLayer: {
        event: eventName,
        ...params
      },
      dataLayerName: 'webDataLayer'
    })
  }
  const fa = getFirebaseAnalytics()

  if (!fa) {
    eventQueue.push({ eventName, params })
  } else {
    logEvent(getFirebaseAnalytics(), eventName as string, params)
  }
}

export type bookingEventType =
  | 'book_step_1'
  | 'book_step_1_change_type'
  | 'book_step_1_search_location'
  | 'book_step_1_select_city'
  | 'book_step_1_select_location'
  | 'book_step_1_select_dates'
  | 'view_items_list'
  | 'select_item'
  | 'add_to_cart'
  | 'book_step_2_select_mileage'
  | 'book_step_2_select_protection'
  | 'book_step_2_select_delivery'
  | 'book_step_2_select_one_way_return_location_value'
  | 'begin_checkout'
  | 'add_payment_info'
  | 'purchase'
  | 'book_step_3_add_voucher'

export const sendBookingAnalyticEvent = async (
  eventName: bookingEventType,
  selectedProduct?: IProduct | null,
  selectedCity?: ICity | null,
  selectedLocation?: ILocation | null,
  selectedScheduleStartAt?: Moment | null,
  selectedScheduleEndAt?: Moment | null,
  proposedBookings?: IBooking[] | null,
  selectedBookingIndex?: number | null,
  selectedBooking?: IBooking | null,
  voucher?: string | null,
  searchField?: string | null,
  searchResult?: number | null
) => {
  try {
    console.debug('analyticsHelper.sendBookingAnalyticEvent', eventName)
    const params: any = {
      instance_brand: configurations.UFO_APP_PLATFORM,
      product_selected: selectedProduct?.reference || undefined,
      location_search_typed: searchField || undefined,
      location_search_results:
        searchResult === 0 ? 0 : searchResult || undefined,
      location_selected_country: selectedCity?.country?.code || undefined,
      location_selected_city: selectedCity?.reference || undefined,
      location_selected: selectedLocation?.reference || undefined,
      date_from: selectedScheduleStartAt
        ? selectedScheduleStartAt.format('YYYYMMDD')
        : undefined,
      date_to: selectedScheduleEndAt
        ? selectedScheduleEndAt.format('YYYYMMDD')
        : undefined,
      time_from: selectedScheduleStartAt
        ? selectedScheduleStartAt.format('HH:mm')
        : undefined,
      time_to: selectedScheduleEndAt
        ? selectedScheduleEndAt.format('HH:mm')
        : undefined,
      booking_length_days:
        selectedScheduleEndAt && selectedScheduleStartAt
          ? selectedScheduleEndAt.diff(selectedScheduleStartAt, 'days')
          : undefined,
      booking_length_exact:
        selectedScheduleEndAt && selectedScheduleStartAt
          ? getExactBookingLength(
            selectedScheduleEndAt,
            selectedScheduleStartAt
          )
          : undefined,
      vehicle_model: selectedBooking?.vehicleModel.reference || undefined,
      vehicle_model_name: selectedBooking?.vehicleModel.name || undefined,
      mileage: selectedBooking?.options?.mileage
        ? getMileageUnitConvertion(
          selectedBooking.options.mileage.unit,
          selectedBooking.options.mileage
        )
        : undefined,
      protection: selectedBooking?.options?.insurances?.value || undefined,
      value: selectedBooking ? `${selectedBooking?.price.amount}` : undefined,
      coupon: voucher,
      transaction_id: (selectedBooking as any)?.reference,
      shipping: (selectedBooking as any)?.reference
        ? selectedBooking?.options?.deliveries?.price.amount || 0
        : undefined,
      payment_type: ['purchase', 'add_payment_info'].includes(eventName)
        ? 'credit_card'
        : undefined,
      currency: selectedBooking?.price.currency || undefined,
      items: []
    }

    if (!selectedBooking && proposedBookings && proposedBookings[0]) {
      params.currency = proposedBookings[0].price.currency
    }

    params.items = (selectedBooking
      ? [selectedBooking]
      : proposedBookings || []
    ).map((b, i) => ({
      item_id: b.vehicleModel?.reference || '',
      item_name: b.vehicleModel?.name || '',
      item_brand: b.vehicleModel?.manufacturer || '',
      item_category: b.product.reference || '',
      item_category2: b.location?.city?.country?.code || '',
      item_category3: b.location?.city?.reference || '',
      item_category4: b.location?.reference || '',
      item_category5: b.schedule
        ? `${moment(b.schedule.endAt).diff(b.schedule.startAt, 'days')}`
        : '',
      item_variant: b.schedule
        ? moment(b.schedule.startAt).format('YYYYMMDD')
        : '',
      price: `${b.price?.amountPerUnit}`,
      quantity: `${b.schedule?.duration}`
    }))
    sendEvent(eventName, params)
  } catch (error) {
    console.error(error)
  }
}

const handleConsent = (newConsent:any) => {
  (window as any).gtag('consent', 'update', {
    ad_storage: newConsent.STORAGE === 'ACCEPT' ? 'granted' : 'denied',
    analytics_storage: newConsent.ANALYTICS === 'ACCEPT' ? 'granted' : 'denied',
    ad_user_data: newConsent.MARKETING === 'ACCEPT' ? 'granted' : 'denied',
    ad_personalization: newConsent.PERSONALIZATION === 'ACCEPT' ? 'granted' : 'denied'
  })

  loadGTMContainer(newConsent.ANALYTICS)
}

// Check if the code is running in a browser environment
if (
  typeof window !== 'undefined' &&
  typeof (window as any).Osano !== 'undefined' &&
  typeof (window as any).Osano.cm !== 'undefined' &&
  typeof (window as any).Osano.cm.addEventListener === 'function' &&
  (process.env.REACT_APP_PLATFORM === 'uft' || process.env.REACT_APP_PLATFORM === 'ufodrive')
) {
  const osanoCm = (window as any).Osano.cm
  // Define the event listener function
  const consentChangeListener = (change: any) => {
    handleConsent(change)
  }
  // Add event listener for osano-cm-consent-saved
  osanoCm.addEventListener('osano-cm-consent-saved', consentChangeListener)
}

export const addAnalyticsEvent = async (eventName: string, params: any) => {
  try {
    console.debug('analyticsHelper.addAnalyticsEvent', eventName)

    if (
      process.env.REACT_APP_ENV === 'production' &&
      process.env.REACT_APP_PLATFORM === 'ufodrive'
    ) {
      ReactPixel.trackCustom(eventName, params)
    }
  } catch (error) {
    console.error('analyticsHelper.addAnalyticsEvent', error)
  }
}

export const initialiseAnalyticsUser = async (
  user: IUser,
  partnerCode?: string
): Promise<void> => {
  try {
    console.debug('analyticsHelper.initialiseAnalyticsUser', user.reference)

    setUserId(getFirebaseAnalytics(), user.reference)
    setUserProperties(getFirebaseAnalytics(), {
      role:
        user.roleReference ||
        (user as any)?.role?.reference ||
        (user as any)?.role,
      status: user.status,
      partnerCode
    })

    if (
      process.env.REACT_APP_PLATFORM === 'uft' ||
      process.env.REACT_APP_PLATFORM === 'ufodrive'
    ) {
      const options = {
        autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
        debug: false // enable logs
      }
      ReactPixel.init('658866822292578', undefined, options)
      addAnalyticsEvent('/booking/book', {})
    }
  } catch (error) {
    console.error('analyticHelper.initialiseAnalyticsUser', error)
  }
}

const getExactBookingLength = (endAt: Moment, startAt: Moment) => {
  const duration = moment.duration(moment(endAt).diff(moment(startAt)))

  const days = Math.floor(duration.asDays())
  const daysFormatted = days

  const hours = duration.hours()
  const hoursFormatted = hours

  return `${daysFormatted}:${hoursFormatted}`
}

const getMileageUnitConvertion = (
  mileageUnit: string | undefined,
  mileage: IMileage | undefined
) => {
  if (mileageUnit?.toLowerCase() === 'km') {
    return Math.round((mileage?.value || 0) * 0.621371).toString()
  } else {
    return (mileage?.value || 0).toString()
  }
}

export const sendDescriptionEvent = async (
  eventName: 'click_info',
  descriptionReference: string
) => {
  try {
    console.debug('analyticsHelper.sendDescriptionEvent', eventName)
    const params: any = {
      instance_brand: configurations.UFO_APP_PLATFORM,
      info: descriptionReference
    }

    sendEvent(eventName, params)
  } catch (error) {
    console.error(error)
  }
}

export const viewAnalyticsScreen = async (screenName: string) => {
  try {
    console.debug('analyticsHelper.viewAnalyticsScreen', screenName)
    setCurrentScreen(getFirebaseAnalytics(), screenName)
    const performedAt = moment().toDate()
    const payload = {
      severity: 'info',
      code: '0',
      action: 'Navigation/NAVIGATE',
      message: screenName,
      performed_at: performedAt
    }
    postToApi('/v2/user_experiences', payload, true)

    if (
      process.env.REACT_APP_ENV === 'production' &&
      process.env.REACT_APP_PLATFORM === 'ufodrive'
    ) {
      ReactPixel.track('ViewContent', { content_name: screenName })
    }
  } catch (error) {
    console.error('analyticsHelper.viewAnalyticsScreen', error)
  }
}
